// src/pages/payment/PaymentCancelled.jsx
import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { 
  XCircle, 
  ArrowLeft, 
  RefreshCw, 
  Phone, 
  Mail,
  AlertTriangle,
  CreditCard
} from 'lucide-react';

// Header is now in PublicLayout
import { usePayment } from '../../features/october/hooks/usePayment';

const PaymentCancelled = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getOrder, handlePaymentCancellation } = usePayment();
  const [order, setOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState('cancelled'); // 'cancelled' or 'failed'

  useEffect(() => {
    const handlePaymentResult = async () => {
      try {
        setIsLoading(true);

        const orderId = searchParams.get('order');
        const status = searchParams.get('payment_status');

        // Use a local variable — NOT the state — to control flow inside this async function.
        // State updates are batched and the value would still be 'cancelled' if we read the state.
        const urlIsFailed = status === 'FAILED';
        if (urlIsFailed) setPaymentStatus('failed');

        if (orderId) {
          const orderData = await getOrder(orderId);
          setOrder(orderData);

          // Only mark as cancelled when it wasn't a hard failure
          if (!urlIsFailed) {
            await handlePaymentCancellation(orderId);
          }
        }
      } catch (err) {
        console.error('Error handling payment result:', err);
      } finally {
        setIsLoading(false);
      }
    };

    handlePaymentResult();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRetryPayment = () => {
    if (order) {
      // Navigate back to checkout with the order details pre-filled
      navigate('/checkout', { 
        state: { 
          retryOrder: order,
          prefillData: {
            email: order.customer.email,
            firstName: order.customer.firstName,
            lastName: order.customer.lastName,
            phone: order.customer.phone,
            address: order.shippingAddress.address,
            city: order.shippingAddress.city,
            province: order.shippingAddress.province,
            postalCode: order.shippingAddress.postalCode
          }
        }
      });
    } else {
      navigate('/checkout');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-brand-dark flex items-center justify-center">
        <div className="text-center">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            className="w-8 h-8 border-2 border-brand-gold border-t-transparent rounded-full mx-auto mb-4"
          />
          <p className="text-white font-body font-light">Loading...</p>
        </div>
      </div>
    );
  }

  const isFailed = paymentStatus === 'failed';
  const title = isFailed ? 'Payment Failed' : 'Payment Cancelled';
  const description = isFailed 
    ? 'There was an issue processing your payment. Please try again.'
    : 'Your payment was cancelled. No charges were made to your account.';

  return (
    <div className="min-h-screen bg-brand-dark">
      <div className="pt-20 pb-16 bg-brand-dark min-h-screen">
        <div className="max-w-4xl mx-auto px-4">
          
          {/* Error Animation */}
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5, ease: "backOut" }}
            className="text-center mb-12"
          >
            <div className="relative mb-6">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, duration: 0.5 }}
                className={`w-20 h-20 ${isFailed ? 'bg-red-500' : 'bg-orange-500'} rounded-full flex items-center justify-center mx-auto`}
              >
                {isFailed ? (
                  <XCircle size={40} className="text-white" />
                ) : (
                  <AlertTriangle size={40} className="text-white" />
                )}
              </motion.div>
              
              {/* Animated rings */}
              <motion.div
                initial={{ scale: 0, opacity: 1 }}
                animate={{ scale: 2, opacity: 0 }}
                transition={{ delay: 0.5, duration: 1.5 }}
                className={`absolute inset-0 w-20 h-20 border-2 ${isFailed ? 'border-red-500' : 'border-orange-500'} rounded-full mx-auto`}
              />
            </div>
            
            <motion.h1
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="text-3xl lg:text-4xl font-light text-white mb-4 tracking-wide"
            >
              {title}
            </motion.h1>
            
            <motion.p
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="text-gray-400 font-light text-lg"
            >
              {description}
            </motion.p>
          </motion.div>

          {/* Order Information (if available) */}
          {order && (
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="bg-black/90 backdrop-blur-xl rounded-3xl border border-white/10 p-8 mb-8"
            >
              <h2 className="text-xl font-light text-white mb-6 tracking-wide">Order Information</h2>
              
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-white font-light mb-4">Order Details</h3>
                  <div className="space-y-3 text-gray-400 font-light">
                    <div className="flex justify-between">
                      <span>Order Number:</span>
                      <span className="text-white">{order.orderNumber}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Total Amount:</span>
                      <span className="text-white">R{order.total.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Status:</span>
                      <span className={`${isFailed ? 'text-red-400' : 'text-orange-400'}`}>
                        {isFailed ? 'Payment Failed' : 'Cancelled'}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-white font-light mb-4">Items</h3>
                  <div className="space-y-2">
                    {order.items.slice(0, 3).map((item) => (
                      <div key={item.id} className="flex justify-between text-sm">
                        <span className="text-gray-400 truncate">{item.name}</span>
                        <span className="text-white ml-2">×{item.quantity}</span>
                      </div>
                    ))}
                    {order.items.length > 3 && (
                      <p className="text-gray-500 text-sm">
                        +{order.items.length - 3} more items
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Action Buttons */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="flex flex-wrap gap-4 justify-center mb-12"
          >
            <button
              onClick={handleRetryPayment}
              className="flex items-center gap-2 px-8 py-3 bg-brand-gold hover:bg-brand-gold-light text-brand-dark rounded-xl font-body font-medium transition-colors"
            >
              <RefreshCw size={16} />
              Try Again
            </button>
            
            <Link
              to="/kganya-scents"
              className="flex items-center gap-2 px-8 py-3 bg-brand-charcoal hover:bg-brand-charcoal-light text-white rounded-xl font-body font-light transition-colors border border-brand-gold/20"
            >
              <ArrowLeft size={16} />
              Back to Shop
            </Link>
          </motion.div>

          {/* Common Payment Issues */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.7 }}
            className="bg-black/90 backdrop-blur-xl rounded-3xl border border-white/10 p-8 mb-8"
          >
            <h2 className="text-xl font-light text-white mb-6 tracking-wide">
              {isFailed ? 'Common Payment Issues' : 'Why This Might Have Happened'}
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2 flex-shrink-0" />
                  <div>
                    <h3 className="text-white font-light mb-1">Insufficient Funds</h3>
                    <p className="text-gray-400 font-light text-sm">
                      Please check your account balance and try again
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2 flex-shrink-0" />
                  <div>
                    <h3 className="text-white font-light mb-1">Card Declined</h3>
                    <p className="text-gray-400 font-light text-sm">
                      Contact your bank or try a different payment method
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2 flex-shrink-0" />
                  <div>
                    <h3 className="text-white font-light mb-1">Network Issues</h3>
                    <p className="text-gray-400 font-light text-sm">
                      Temporary connection problems may have occurred
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2 flex-shrink-0" />
                  <div>
                    <h3 className="text-white font-light mb-1">Security Check</h3>
                    <p className="text-gray-400 font-light text-sm">
                      Your bank may require additional verification
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2 flex-shrink-0" />
                  <div>
                    <h3 className="text-white font-light mb-1">Expired Card</h3>
                    <p className="text-gray-400 font-light text-sm">
                      Please check your card expiry date
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2 flex-shrink-0" />
                  <div>
                    <h3 className="text-white font-light mb-1">Changed Mind</h3>
                    <p className="text-gray-400 font-light text-sm">
                      No problem! Your items are still in your cart
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Payment Methods */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.8 }}
            className="bg-black/90 backdrop-blur-xl rounded-3xl border border-white/10 p-8 mb-8"
          >
            <h2 className="text-xl font-light text-white mb-6 tracking-wide">Accepted Payment Methods</h2>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="flex items-center gap-2 p-3 bg-white/5 rounded-xl">
                <CreditCard size={16} className="text-gray-400" />
                <span className="text-gray-300 font-light text-sm">Credit Cards</span>
              </div>
              <div className="flex items-center gap-2 p-3 bg-white/5 rounded-xl">
                <CreditCard size={16} className="text-gray-400" />
                <span className="text-gray-300 font-light text-sm">Debit Cards</span>
              </div>
              <div className="flex items-center gap-2 p-3 bg-white/5 rounded-xl">
                <CreditCard size={16} className="text-gray-400" />
                <span className="text-gray-300 font-light text-sm">Instant EFT</span>
              </div>
              <div className="flex items-center gap-2 p-3 bg-white/5 rounded-xl">
                <CreditCard size={16} className="text-gray-400" />
                <span className="text-gray-300 font-light text-sm">Mobile Money</span>
              </div>
            </div>
          </motion.div>

          {/* Customer Support */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.9 }}
            className="text-center"
          >
            <div className="bg-black/90 backdrop-blur-xl rounded-3xl border border-white/10 p-8">
              <h3 className="text-white font-light mb-4">Need Assistance?</h3>
              <p className="text-gray-400 font-light mb-6">
                Our support team is here to help you complete your purchase.
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <a
                  href="mailto:support@pontsoyarona.com"
                  className="flex items-center gap-2 px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-2xl font-light transition-colors"
                >
                  <Mail size={16} />
                  Email Support
                </a>
                <a
                  href="tel:+27123456789"
                  className="flex items-center gap-2 px-6 py-3 bg-green-500 hover:bg-green-600 text-white rounded-2xl font-light transition-colors"
                >
                  <Phone size={16} />
                  Call Us
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default PaymentCancelled;